// CookiesProvider — cookies suite root.
//
// Owns: app-cookie store with namespaced keys (`io.cabal-ai.<subPath>`), shared-domain
// resolution for production (cabal-ai.app), and a subscribe/notify channel so multiple
// hooks reading the same cookie stay in sync.
// Mounts at app root, immediately under QueryClient.

'use client';

import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';
import Cookies from 'js-cookie';
import { useSeqLog, LogLevel } from '@/hooks/telemetry';

const CABAL_COOKIE_PREFIX = 'io.cabal-ai';
const CABAL_COOKIE_DOMAIN = '.cabal-ai.app';
const CABAL_COOKIE_ROOT_DOMAIN = 'cabal-ai.app';
const COOKIES_PROVIDER_SEQ_COMPONENT = 'CookiesProvider';

const CABAL_COOKIE_BASE_CONFIG: Cookies.CookieAttributes = {
  path: '/',
  expires: 365,
  sameSite: 'lax',
  secure: process.env.NODE_ENV === 'production',
};

type CookieListener = (value: string | undefined) => void;

interface CookiesContextValue {
  getCookie: (subPath: string) => string | undefined;
  setCookie: (subPath: string, value: string, options?: Cookies.CookieAttributes) => void;
  removeCookie: (subPath: string) => void;
  subscribeToCookie: (subPath: string, listener: CookieListener) => () => void;
}

const CookiesContext = createContext<CookiesContextValue | null>(null);

function normalizeSubPath(subPath: string): string {
  return subPath.trim().replace(/^\.+|\.+$/g, '');
}

function buildCookieName(subPath: string): string {
  const normalizedSubPath = normalizeSubPath(subPath);
  return normalizedSubPath
    ? `${CABAL_COOKIE_PREFIX}.${normalizedSubPath}`
    : CABAL_COOKIE_PREFIX;
}

function getHostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

function shouldUseSharedCookieDomain(hostname: string): boolean {
  return hostname === CABAL_COOKIE_ROOT_DOMAIN || hostname.endsWith(CABAL_COOKIE_DOMAIN);
}

function buildCookieAttributes(options?: Cookies.CookieAttributes): Cookies.CookieAttributes {
  const hostname = getHostname();
  return {
    ...CABAL_COOKIE_BASE_CONFIG,
    ...(shouldUseSharedCookieDomain(hostname) ? { domain: CABAL_COOKIE_DOMAIN } : {}),
    ...options,
  };
}

function getDocumentCookie(): string {
  return typeof document === 'undefined' ? '' : document.cookie;
}

function countCookieNameMatches(name: string): number {
  return getDocumentCookie()
    .split(';')
    .map((cookiePart) => cookiePart.trim().split('=')[0])
    .filter((cookieName) => cookieName === name)
    .length;
}

function getEncodedCookieValueLength(value: string): number {
  return encodeURIComponent(value).length;
}

export function CookiesProvider({ children }: { children: ReactNode }) {
  const listenersRef = useRef<Map<string, Set<CookieListener>>>(new Map());
  const log = useSeqLog(COOKIES_PROVIDER_SEQ_COMPONENT);

  const notifyCookieListeners = useCallback((subPath: string, value: string | undefined) => {
    const normalizedSubPath = normalizeSubPath(subPath);
    const listeners = listenersRef.current.get(normalizedSubPath);
    if (!listeners) return;
    listeners.forEach((listener) => listener(value));
  }, []);

  const getCookie = useCallback((subPath: string) => {
    try {
      return Cookies.get(buildCookieName(subPath));
    } catch {
      return undefined;
    }
  }, []);

  const setCookie = useCallback(
    (subPath: string, value: string, options?: Cookies.CookieAttributes) => {
      const name = buildCookieName(subPath);
      const attributes = buildCookieAttributes(options);
      try {
        Cookies.set(name, value, attributes);
      } catch {
        // Best effort only. Cookie failures must not block the UX.
      }
      const persistedValue = Cookies.get(name);
      log(
        'Cabal cookie set attempted',
        {
          subPath,
          cookieName: name,
          hostname: getHostname(),
          usesSharedDomain: attributes.domain === CABAL_COOKIE_DOMAIN,
          persisted: persistedValue === value,
          valueLength: value.length,
          encodedValueLength: getEncodedCookieValueLength(value),
          persistedValueLength: persistedValue?.length ?? null,
          matchingCookieCount: countCookieNameMatches(name),
          documentCookieLength: getDocumentCookie().length,
        },
        persistedValue === value ? LogLevel.Debug : LogLevel.Warning,
      );
      notifyCookieListeners(subPath, value);
    },
    [log, notifyCookieListeners],
  );

  const removeCookie = useCallback(
    (subPath: string) => {
      const name = buildCookieName(subPath);
      const attributes = buildCookieAttributes();
      try {
        Cookies.remove(name, attributes);
      } catch {
        // Best effort only.
      }
      log(
        'Cabal cookie remove attempted',
        {
          subPath,
          cookieName: name,
          hostname: getHostname(),
          usesSharedDomain: attributes.domain === CABAL_COOKIE_DOMAIN,
          removed: Cookies.get(name) === undefined,
        },
        LogLevel.Debug,
      );
      notifyCookieListeners(subPath, undefined);
    },
    [log, notifyCookieListeners],
  );

  const subscribeToCookie = useCallback((subPath: string, listener: CookieListener) => {
    const normalizedSubPath = normalizeSubPath(subPath);
    const existingListeners = listenersRef.current.get(normalizedSubPath);
    if (existingListeners) {
      existingListeners.add(listener);
    } else {
      listenersRef.current.set(normalizedSubPath, new Set([listener]));
    }
    return () => {
      const listeners = listenersRef.current.get(normalizedSubPath);
      if (!listeners) return;
      listeners.delete(listener);
      if (listeners.size === 0) {
        listenersRef.current.delete(normalizedSubPath);
      }
    };
  }, []);

  const contextValue = useMemo<CookiesContextValue>(
    () => ({ getCookie, setCookie, removeCookie, subscribeToCookie }),
    [getCookie, removeCookie, setCookie, subscribeToCookie],
  );

  return <CookiesContext.Provider value={contextValue}>{children}</CookiesContext.Provider>;
}

export function useCookiesContext(): CookiesContextValue {
  const ctx = useContext(CookiesContext);
  if (!ctx) throw new Error('CookiesProvider not mounted');
  return ctx;
}
